import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getGitHubStats } from '../../../services/githubService';

const SocialConnect = () => {
  const [socialPlatforms, setSocialPlatforms] = useState([
    {
      id: 1,
      name: 'GitHub',
      handle: '@Mustan-dev',
      followers: '2.3K',
      description: 'Open source projects, code repositories, and technical contributions. Status: Active',
      icon: 'Github',
      color: 'slate',
      url: 'https://github.com/Mustan-dev',
      stats: [
        { label: 'Repositories', value: '23' },
        { label: 'Contributions', value: '247' },
        { label: 'Commits', value: '217' },
        { label: 'Visitors', value: '193' },
        { label: 'Following', value: '3' }
      ]
    },
    {
      id: 2,
      name: 'LinkedIn',
      handle: 'Mustan Aliev',
      followers: '8',
      description: 'Professional updates, industry insights, and career highlights',
      icon: 'Linkedin',
      color: 'blue',
      url: 'https://www.linkedin.com/in/mustan-aliev-774732382/',
      stats: [
        { label: 'Connections', value: '120+' },
        { label: 'Posts', value: '25' },
        { label: 'Articles', value: '1' }
      ]
    },
    {
      id: 3,
      name: 'Instagram',
      handle: '@mustan.dev',
      followers: '1.2K',
      description: 'Daily development insights, project updates, and tech content',
      icon: 'Instagram',
      color: 'pink',
      url: 'https://www.instagram.com/mustan.dev/',
      stats: [
        { label: 'Followers', value: '1.2K' },
        { label: 'Posts', value: '45' },
        { label: 'Following', value: '340' }
      ]
    }
  ]);

  useEffect(() => {
    let mounted = true;
    async function fetchGitHub() {
      try {
        const data = await getGitHubStats();
        if (!mounted || !data) return;

        const reposCount = data.repositories?.totalCount ?? socialPlatforms[0].stats[0].value;
        const contributions = data.contributionsCollection?.contributionCalendar?.totalContributions ?? socialPlatforms[0].stats[1].value;
        const commits = data.contributionsCollection?.totalCommitContributions ?? socialPlatforms[0].stats[2].value;
        const followers = data.followers?.totalCount ?? socialPlatforms[0].followers;
        const following = data.following?.totalCount ?? socialPlatforms[0].stats[4].value;

        setSocialPlatforms((prev) => prev.map((p) => p.name === 'GitHub' ? {
          ...p,
          followers: followers?.toString() ?? p.followers,
          stats: [
            { label: 'Repositories', value: reposCount?.toString() ?? p.stats[0].value },
            { label: 'Contributions', value: contributions?.toString() ?? p.stats[1].value },
            { label: 'Commits', value: commits?.toString() ?? p.stats[2].value },
            { label: 'Visitors', value: p.stats[3].value },
            { label: 'Following', value: following?.toString() ?? p.stats[4].value }
          ]
        } : p));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Failed to fetch GitHub stats for SocialConnect:', err?.message || err);
      }
    }

    fetchGitHub();
    return () => { mounted = false; };
  }, []);

  const getColorClasses = (color) => {
    const colors = {
      slate: {
        bg: 'from-slate-600 to-slate-700',
        hover: 'hover:from-slate-700 hover:to-slate-800',
        light: 'bg-slate-100 text-slate-700'
      },
      blue: {
        bg: 'from-blue-600 to-blue-700',
        hover: 'hover:from-blue-700 hover:to-blue-800',
        light: 'bg-blue-100 text-blue-700'
      },
      cyan: {
        bg: 'from-cyan-500 to-cyan-600',
        hover: 'hover:from-cyan-600 hover:to-cyan-700',
        light: 'bg-cyan-100 text-cyan-700'
      },
      emerald: {
        bg: 'from-emerald-600 to-emerald-700',
        hover: 'hover:from-emerald-700 hover:to-emerald-800',
        light: 'bg-emerald-100 text-emerald-700'
      },
      pink: {
        bg: 'from-pink-600 to-pink-700',
        hover: 'hover:from-pink-700 hover:to-pink-800',
        light: 'bg-pink-100 text-pink-700'
      }
    };
    return colors?.[color] || colors?.slate;
  };

  return (
    <section className="py-20 bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">Connect on Social Media</h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Follow my journey, get updates on new projects, and join the conversation across different platforms. Each platform offers unique content and insights.
          </p>
        </div>

        {/* Social Platforms Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {socialPlatforms?.map((platform) => {
            const colorClasses = getColorClasses(platform?.color);
            return (
              <div key={platform?.id} className="bg-slate-800 rounded-2xl p-8 hover:bg-slate-750 transition-all duration-300 group">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div className={`w-14 h-14 bg-gradient-to-r ${colorClasses?.bg} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <Icon name={platform?.icon} size={28} color="white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{platform?.name}</h3>
                      <p className="text-slate-400">{platform?.handle}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-white">{platform?.followers}</div>
                    <div className="text-sm text-slate-400">Followers</div>
                  </div>
                </div>

                <p className="text-slate-300 mb-6 leading-relaxed">{platform?.description}</p>

                <div className="grid grid-cols-3 gap-4 mb-6">
                  {platform?.stats?.map((stat, index) => (
                    <div key={index} className="text-center">
                      <div className="text-lg font-bold text-white">{stat?.value}</div>
                      <div className="text-xs text-slate-400">{stat?.label}</div>
                    </div>
                  ))}
                </div>

                <div className="flex space-x-3">
                  <Button
                    asChild
                    variant="default"
                    className={`flex-1 bg-gradient-to-r ${colorClasses?.bg} ${colorClasses?.hover} text-white border-0`}
                    iconName="UserPlus"
                    iconPosition="left"
                  >
                    <a href={platform?.url} target="_blank" rel="noopener noreferrer">Follow</a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                    iconName="ExternalLink"
                  >
                    <a href={platform?.url} target="_blank" rel="noopener noreferrer">Visit</a>
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Newsletter Signup */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-center">
          <div className="max-w-2xl mx-auto">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Mail" size={32} color="white" />
            </div>
            <h3 className="text-2xl font-bold mb-4">Stay Updated</h3>
            <p className="text-blue-100 mb-8 text-lg">Get monthly updates about new projects, technical articles, and industry insights delivered straight to your inbox. No spam, just valuable content.</p>
            
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input type="email" placeholder="Enter your email address" className="flex-1 px-4 py-3 rounded-lg text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-white/50" />
              <Button variant="default" className="bg-white text-blue-600 hover:bg-blue-50 border-0 font-semibold" iconName="Send" iconPosition="right">Subscribe</Button>
            </div>
            
            <p className="text-blue-200 text-sm mt-4">Join 1,200+ developers who get my monthly newsletter</p>
          </div>
        </div>

        {/* Social Proof */}
        <div className="mt-16 text-center">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <div className="text-3xl font-bold text-cyan-400 mb-2">12K+</div>
              <div className="text-slate-400">Total Followers</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-400 mb-2">180+</div>
              <div className="text-slate-400">Posts & Articles</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-400 mb-2">50K+</div>
              <div className="text-slate-400">Content Views</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-400 mb-2">95%</div>
              <div className="text-slate-400">Engagement Rate</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialConnect;
